import { z } from 'zod';
/**
 * The ceiling on rows returned in ONE roster response.
 *
 * The roster does not paginate — a warden counting heads in a car park cannot click
 * "next" — so a cap is the only protection against a pathological tenant pulling the
 * whole building onto a phone. 2,000 is far past any single site's simultaneous
 * occupancy and about 200 KB of JSON. When it bites, `isTruncated` says so ON THE WIRE:
 * a list quietly missing its tail during a fire is the worst thing this milestone could
 * ship.
 */
export const EVACUATION_ROSTER_MAX_ROWS = 2000;
/**
 * One person on the roster.
 *
 * DELIBERATELY ABSENT, each for its own reason:
 *   email / phone   — LOG-11 and docs/008 S7. A roster is a count of people, not a
 *                     contact list, and this response is polled every few seconds.
 *   `answers`       — twenty check-in answers per row, downloaded on a cellular
 *                     connection during an evacuation, to render twenty names.
 *   `checkedOutAt`  — every row on this roster is checked in, by definition.
 *   `inviteId`      — how the arrival was arranged has nothing to do with finding them.
 *
 * `visitorName` and `hostName` are ALREADY-COMPOSED display strings, not first/last
 * pairs: this DTO exists to be rendered, and the visitor log's `hostSummaryDTOSchema`
 * shape would drag a department and a title onto a phone for no reader.
 */
export const evacuationEntryDTOSchema = z.object({
    /** The `VisitorVisit.id` — the id `POST /api/web/visits/:id/mark-safe` takes. */
    visitId: z.string().min(1),
    visitorName: z.string().min(1),
    visitorCompany: z.string().nullable(),
    hostName: z.string().nullable(),
    locationId: z.string().min(1),
    locationName: z.string().nullable(),
    checkedInAt: z.string().datetime(),
    /** When this person was accounted for, or `null` while they are outstanding. */
    markedSafeAt: z.string().datetime().nullable(),
});
/**
 * The whole roster, as ONE object in a `DataEnvelope` — never a `ListEnvelope`.
 *
 * `ListEnvelope.meta` is `{ page, pageSize, total }`, and this response has no page and no
 * page size; filling them with `1` and the cap would be a lie in the shape of a contract.
 * `searchHosts` in `./visit.js` set the precedent for a capped, unpaginated list returning
 * `DataEnvelope`.
 *
 * `total` and `safeCount` are counted over the WHOLE matching set, not over `entries` — so
 * the "N of M accounted for" a warden reads stays correct even when the cap truncated the
 * list beneath it.
 */
export const evacuationRosterDTOSchema = z.object({
    /** The location this roster was asked for, or `null` for every location in scope. */
    locationId: z.string().min(1).nullable(),
    /** When the server answered. The screen shows it so an operator can tell a live roster
     *  from one a hidden tab stopped polling ten minutes ago. */
    generatedAt: z.string().datetime(),
    /** Everyone currently checked in, in scope — counted, not `entries.length`. */
    total: z.number().int().nonnegative(),
    /** How many of `total` carry a non-null `markedSafeAt` — likewise counted. */
    safeCount: z.number().int().nonnegative(),
    /** `true` when `total` exceeded `EVACUATION_ROSTER_MAX_ROWS` and `entries` is a prefix
     *  of the roster rather than all of it. The screen MUST render this. */
    isTruncated: z.boolean(),
    entries: z.array(evacuationEntryDTOSchema),
});
/**
 * `GET /api/web/evacuation/roster` — the whole query.
 *
 * ONE DIMENSION, ON PURPOSE. No `status` (every roster row is checked in), no date range
 * (the roster is "now"), no `q` (a warden scanning for a name reads the screen; a search
 * box that hides rows during an evacuation is a hazard, not a feature). `locationId` is
 * optional and, when given, is INTERSECTED with the caller's own allowed locations
 * server-side — it can only ever narrow scope, never widen it.
 */
export const evacuationRosterQuerySchema = z.object({
    locationId: z.string().min(1).optional(),
});
/**
 * POST /api/web/evacuation/alert-hosts — the warden's "tell every host with a visitor on site".
 * `locationId` narrows exactly like the roster's query (intersected with the caller's own scope
 * server-side; it can never widen it).
 */
export const evacuationHostAlertRequestSchema = z.object({
    locationId: z.string().min(1).optional(),
});
/** What the alert did. Counts only — never a host's or a visitor's name (S7). */
export const evacuationHostAlertDTOSchema = z.object({
    /** One id per press. NotificationDelivery.subjectId and the audit row's targetId carry it. */
    alertId: z.string().uuid(),
    sentAt: z.string().datetime(),
    /** Visits checked in, in scope, when the alert went out. */
    visitsOnSite: z.number().int().nonnegative(),
    /** Distinct hosts of those visits. */
    hosts: z.number().int().nonnegative(),
    /** Hosts at least one of whose phones accepted the push. */
    hostsAlerted: z.number().int().nonnegative(),
    /** false: the tenant's plan does not include InstaCheckin Express (`visitor.mobile_app`), so nothing was pushed. */
    mobileAppEnabled: z.boolean(),
});
//# sourceMappingURL=evacuation.js.map